/**
 * Claude Haiku on Bedrock, streaming and non-streaming.
 *
 * Bedrock rather than the Anthropic API because all AI spend for this account
 * is already consolidated there. aws4fetch rather than the AWS SDK because
 * SigV4 is the only part of the SDK a Worker needs and the rest is ~1 MB.
 *
 * The system prompt is marked cacheable on every call. It is identical across
 * calls, so the 5-minute ephemeral cache turns it from $1/MTok into $0.10/MTok
 * read. The user message carries tenant data and is NEVER cached — a cache
 * entry is keyed by prefix, and a shared prefix across tenants is exactly how
 * one tenant's data would end up priming another's request.
 */

import { AwsClient } from "aws4fetch";
import { decodeEventStream, textDelta } from "./event-stream.js";
import {
  REQUEST_METADATA_HEADER,
  ZERO_USAGE,
  addUsage,
  extractUsage,
  requestMetadata,
  type AiAttribution,
  type TokenUsage,
} from "./attribution.js";

export const DEFAULT_BEDROCK_REGION = "us-east-1";

/**
 * Haiku 4.5 as a cross-region inference profile (the `us.` prefix).
 * The bare model id is not invocable on-demand in every region; the profile is.
 */
export const DEFAULT_MODEL_ID = "us.anthropic.claude-haiku-4-5-20251001-v1:0";

/**
 * Default wall-clock ceiling for one call. A console user watching tokens
 * arrive will wait this long, so it is the right default for streamChat.
 *
 * It is the wrong default for a call whose output arrives all at once and sits
 * behind other work: the MCP composites and the carrier_ask router both pass
 * `timeoutMs: 12_000` instead. A composite that takes 30s to fail is a worse
 * experience than one that gives up at 12s and shows the measured figures it
 * already has.
 */
export const BEDROCK_TIMEOUT_MS = 30_000;

/**
 * Attempts one Bedrock call gets, deadline permitting. Four, not ten.
 *
 * aws4fetch's own `fetch` retries a 429 or 5xx ten times by default, sleeping
 * `Math.random() * 50 * 2**attempt` ms between them and checking NO abort
 * signal while it sleeps. Under a sustained throttle that loop runs for tens of
 * seconds, so `timeoutMs` was a number we printed rather than a deadline we
 * kept: the controller aborted on time and the call returned whenever the
 * current sleep happened to end.
 *
 * So the client is constructed with `retries: 0` and the policy lives here,
 * where the sleeps are cancellable and every one of them is checked against the
 * deadline before it starts. Retrying a throttle is right; retrying it past the
 * time the caller said it would wait is not.
 */
export const MAX_ATTEMPTS = 4;

/** First backoff step. Doubles per attempt, with full jitter. */
export const INITIAL_RETRY_MS = 100;

/**
 * Time a retry must leave on the clock to be worth taking.
 *
 * Sleeping right up to the deadline and firing a request that is aborted before
 * it can be signed spends the whole remaining budget to learn nothing. Below
 * this the loop stops and reports the throttle it already has.
 */
const MIN_ATTEMPT_BUDGET_MS = 250;

export interface BedrockCredentials {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken?: string;
  region?: string;
  modelId?: string;
}

export interface ChatTurn {
  role: "user" | "assistant";
  content: string;
}

/** One Anthropic tool definition, optionally marked as a cache breakpoint. */
export interface BedrockToolDefinition {
  name: string;
  description: string;
  input_schema: unknown;
  /**
   * Set on the LAST tool to cache the whole tools array. The carrier_ask router
   * sends ~123 tool definitions on every call, which is far more input than the
   * system prompt, so caching the array is where most of its saving comes from.
   */
  cache_control?: { type: "ephemeral" };
}

export interface BedrockRequest {
  system: string;
  messages: ChatTurn[];
  maxTokens: number;
  /**
   * Who this call is for. Sent as requestMetadata, which reaches model
   * invocation LOGS and never the bill. The bill is separated by the
   * inference profile in modelId. See attribution.ts.
   */
  attribution?: AiAttribution;
  /** Called with the token counts once known. Never throws into the caller. */
  onUsage?: (usage: TokenUsage) => void;
  /** Anthropic tool definitions. When set, the model is forced to call one. */
  tools?: BedrockToolDefinition[];
  temperature?: number;
  /** Wall-clock ceiling for this call. Defaults to BEDROCK_TIMEOUT_MS. */
  timeoutMs?: number;
}

/** Thrown so callers can map throttling to a 429 rather than a generic 502. */
export class BedrockRateLimitError extends Error {
  readonly isRateLimit = true;
  /**
   * The raw `retry-after` header, when Bedrock sent one. Callers surface it as
   * the delay they tell the user to wait; without it they have to guess.
   */
  readonly retryAfter: string | null;
  constructor(message: string, retryAfter: string | null = null) {
    super(message);
    this.name = "BedrockRateLimitError";
    this.retryAfter = retryAfter;
  }
}

export class BedrockError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "BedrockError";
  }
}

function client(creds: BedrockCredentials): { aws: AwsClient; region: string; modelId: string } {
  const region = creds.region ?? DEFAULT_BEDROCK_REGION;
  return {
    aws: new AwsClient({
      accessKeyId: creds.accessKeyId,
      secretAccessKey: creds.secretAccessKey,
      ...(creds.sessionToken ? { sessionToken: creds.sessionToken } : {}),
      region,
      service: "bedrock",
      // See MAX_ATTEMPTS. aws4fetch's retry loop cannot be cancelled, so this
      // layer owns the policy instead. Changing this back to the default 10
      // silently restores the ~50s stall that timeoutMs is supposed to prevent.
      retries: 0,
    }),
    region,
    modelId: creds.modelId ?? DEFAULT_MODEL_ID,
  };
}

function body(req: BedrockRequest): Record<string, unknown> {
  return {
    anthropic_version: "bedrock-2023-05-31",
    max_tokens: req.maxTokens,
    ...(req.temperature !== undefined ? { temperature: req.temperature } : {}),
    system: [{ type: "text", text: req.system, cache_control: { type: "ephemeral" } }],
    messages: req.messages.map((m) => ({ role: m.role, content: m.content })),
    ...(req.tools
      ? { tools: req.tools, tool_choice: { type: "any" as const } }
      : {}),
  };
}

/**
 * Headers for one call, including the attribution metadata.
 *
 * AWS requires `X-Amzn-Bedrock-Request-Metadata` to appear in the SigV4
 * SignedHeaders list, and rejects the request with InvalidSignatureException if
 * it does not. aws4fetch satisfies that by default: it signs every header
 * except a fixed UNSIGNABLE_HEADERS set (authorization, content-type,
 * content-length, user-agent, presigned-expires, expect, x-amzn-trace-id,
 * range, connection) and this header is not one of them.
 *
 * So no `allHeaders` flag. Setting it would ALSO sign content-type,
 * content-length and user-agent, which are excluded deliberately because a
 * runtime or proxy can rewrite them after signing. That turns a correct request
 * into an intermittent, load-dependent InvalidSignatureException, which is a
 * much worse failure than a consistent one.
 */
function headersFor(
  attribution: AiAttribution | undefined,
  accept: string,
): Record<string, string> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: accept,
  };

  const meta = attribution ? requestMetadata(attribution) : null;
  if (meta) headers[REQUEST_METADATA_HEADER] = JSON.stringify(meta);

  return headers;
}

/** Report usage without letting a caller's callback break the model call. */
function reportUsage(req: BedrockRequest, usage: TokenUsage): void {
  if (!req.onUsage) return;
  try {
    req.onUsage(usage);
  } catch {
    // Metering must never fail a paid call that already succeeded.
  }
}

async function fail(resp: Response): Promise<never> {
  const text = await resp.text().catch(() => "");
  if (resp.status === 429 || /throttl/i.test(text)) {
    throw new BedrockRateLimitError(
      text || "Bedrock throttled the request",
      resp.headers.get("retry-after"),
    );
  }
  throw new BedrockError(text || `Bedrock returned HTTP ${resp.status}`, resp.status);
}

/**
 * The error a caller sees when the deadline expired with nothing to report.
 *
 * Both the name and the message are load-bearing: `classifyModelError` in
 * @carrier/ocs-client maps `TimeoutError` and /timed out/ to the `timeout`
 * reason, which is deliberately NOT in PERMANENT_FALLBACK_REASONS. A deadline
 * is weather. Rename this and a throttled minute starts paging an operator.
 */
function timedOut(): Error {
  const err = new Error("Bedrock call timed out before a usable response");
  err.name = "TimeoutError";
  return err;
}

/** Sleep that the deadline can cut short. Resolves on abort rather than throwing. */
function sleep(ms: number, signal: AbortSignal): Promise<void> {
  if (signal.aborted) return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer);
      signal.removeEventListener("abort", done);
      resolve();
    };
    const timer = setTimeout(done, ms);
    signal.addEventListener("abort", done, { once: true });
  });
}

/** 429 is a throttle, 5xx is Bedrock having a moment. Both are worth one more try. */
function retryable(status: number): boolean {
  return status === 429 || status >= 500;
}

/**
 * Sign and send one request, retrying throttles strictly inside `deadlineAt`.
 *
 * Every caller in this file routes through here, so the declared timeout is the
 * real one on all three surfaces: streamChat, invokeTool and runToolLoop's
 * per-step invokeModelRaw.
 *
 * On expiry the caller sees the LAST throttle rather than a synthetic timeout,
 * because that is the truthful cause and it still carries `retry-after`.
 * `classifyModelError` checks `isRateLimit` first, so it lands on
 * `rate_limited` — transient, not permanent, not a page. Only a deadline that
 * expires with no response at all reports `timeout`.
 *
 * Nothing is left running when this returns: the sleeps are bound to the same
 * signal the request is, so an expired deadline cancels the pending sleep
 * rather than leaking a timer that wakes up 40s later to fire a request no one
 * is waiting for. That leak is what the old aws4fetch loop did.
 */
async function sendWithRetry(
  aws: AwsClient,
  url: string,
  init: RequestInit,
  signal: AbortSignal,
  deadlineAt: number,
): Promise<Response> {
  let throttled: Response | null = null;

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    if (signal.aborted) break;

    // Re-signed per attempt on purpose: a SigV4 signature carries its datetime,
    // and replaying one after a backoff is how a retry turns a throttle into a
    // much more confusing InvalidSignatureException.
    let resp: Response;
    try {
      resp = await aws.fetch(url, init);
    } catch (err) {
      // The deadline can expire with a retry already in flight, between the
      // budget check above and the response. Without this the AbortError wins
      // and the 429 we already hold is thrown away, so a throttle reports as
      // `timeout` with no retry-after — the caller is told to guess a delay
      // Bedrock had already named. Gated on signal.aborted rather than the
      // error's name because that name is a runtime detail (undici and workerd
      // do not have to agree) while "our own deadline expired" is the fact.
      if (signal.aborted && throttled) return throttled;
      throw err;
    }
    if (!retryable(resp.status)) return resp;
    throttled = resp;

    if (attempt === MAX_ATTEMPTS - 1) break;

    // Full jitter, the same shape aws4fetch used. Spreading retries out is the
    // whole point when a fleet of Workers is throttled by the same quota.
    const backoff = Math.random() * INITIAL_RETRY_MS * 2 ** attempt;
    if (Date.now() + backoff + MIN_ATTEMPT_BUDGET_MS > deadlineAt) break;
    await sleep(backoff, signal);
  }

  if (throttled) return throttled;
  throw timedOut();
}

/**
 * Stream an answer token by token.
 *
 * Yields plain text deltas. The caller decides what wire format they become —
 * apps/api emits SSE, the console re-frames that as an AI SDK UI message
 * stream. Keeping this layer format-free is what lets both exist without a
 * second Bedrock client.
 */
export async function* streamChat(
  creds: BedrockCredentials,
  req: BedrockRequest,
  signal?: AbortSignal,
): AsyncGenerator<string> {
  const { aws, region, modelId } = client(creds);

  const timeoutMs = req.timeoutMs ?? BEDROCK_TIMEOUT_MS;
  const deadlineAt = Date.now() + timeoutMs;

  const controller = new AbortController();
  // One timer for the whole call, deliberately. For a stream `timeoutMs` is a
  // total-duration cap, which is also the only thing that bounds a stream that
  // starts fine and then stalls mid-answer: a deadline armed only around the
  // initial promise would let a half-finished response hang indefinitely. The
  // gap this fix closes is upstream of that — the retry loop before the first
  // byte, which used to run long past the moment this timer fired.
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  // A client that closes the tab must stop the Bedrock call too, or the tenant
  // pays for tokens nobody will ever read.
  const onAbort = () => controller.abort();
  signal?.addEventListener("abort", onAbort, { once: true });

  try {
    const headers = headersFor(req.attribution, "application/vnd.amazon.eventstream");
    const resp = await sendWithRetry(
      aws,
      `https://bedrock-runtime.${region}.amazonaws.com/model/${encodeURIComponent(modelId)}/invoke-with-response-stream`,
      {
        method: "POST",
        headers,
        body: JSON.stringify(body(req)),
        signal: controller.signal,
      },
      controller.signal,
      deadlineAt,
    );

    if (!resp.ok || !resp.body) {
      if (!resp.ok) await fail(resp);
      throw new BedrockError("Bedrock returned an empty stream", 502);
    }

    // Usage arrives split across the stream: message_start carries the input
    // and cache counts, message_delta the output count. Both events were being
    // decoded and thrown away here, which is why token cost was unknowable.
    let usage: TokenUsage = ZERO_USAGE;

    for await (const event of decodeEventStream(resp.body)) {
      if (event.type === "error") {
        const msg = typeof event.message === "string" ? event.message : "stream error";
        if (/throttl/i.test(msg)) throw new BedrockRateLimitError(msg);
        throw new BedrockError(msg, 502);
      }

      usage = addUsage(usage, extractUsage(event));

      const text = textDelta(event);
      if (text !== null) yield text;
    }

    // Reported after the stream drains, so the figure is the whole call. A
    // consumer that abandons the generator early gets no report rather than a
    // partial one, which is the honest outcome: the tokens were still billed,
    // but this layer cannot know the total.
    reportUsage(req, usage);
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", onAbort);
  }
}

export interface ToolCallResult {
  name: string;
  input: Record<string, unknown>;
}

/**
 * One non-streaming call that must answer through a tool.
 *
 * Forcing the tool is the structural guarantee behind the insight cards: a
 * schema with no free-text escape hatch is the only reliable way to stop a
 * model from narrating numbers it was never given.
 */
export async function invokeTool(
  creds: BedrockCredentials,
  req: BedrockRequest,
  signal?: AbortSignal,
): Promise<ToolCallResult | null> {
  const json = (await invokeModelRaw(
    creds,
    body(req),
    { attribution: req.attribution, timeoutMs: req.timeoutMs },
    signal,
  )) as {
    content?: Array<{ type: string; name?: string; input?: Record<string, unknown> }>;
    // Present on the wire and previously undeclared, so never read.
    usage?: Record<string, unknown>;
  };

  reportUsage(req, extractUsage(json));
  const block = json.content?.find((b) => b.type === "tool_use");
  // `input` defaults to {} rather than being required: a tool whose schema has
  // no properties can come back with the key absent, and treating that as "no
  // tool call" would silently drop every zero-argument route.
  return block?.name ? { name: block.name, input: block.input ?? {} } : null;
}

/**
 * Sign and POST one InvokeModel body; return the parsed response.
 *
 * The transport half of a non-streaming call, with none of the opinions.
 * invokeTool forces a tool and reads a single block; runToolLoop lets the model
 * choose and reads the whole content array. Both need the same SigV4 signing,
 * the same attribution header, the same timeout and the same error mapping, and
 * a second copy of that is precisely how the two would drift apart.
 */
export async function invokeModelRaw(
  creds: BedrockCredentials,
  payload: Record<string, unknown>,
  opts: { attribution?: AiAttribution | undefined; timeoutMs?: number | undefined } = {},
  signal?: AbortSignal,
): Promise<unknown> {
  const { aws, region, modelId } = client(creds);

  const timeoutMs = opts.timeoutMs ?? BEDROCK_TIMEOUT_MS;
  const deadlineAt = Date.now() + timeoutMs;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const onAbort = () => controller.abort();
  signal?.addEventListener("abort", onAbort, { once: true });

  try {
    const resp = await sendWithRetry(
      aws,
      `https://bedrock-runtime.${region}.amazonaws.com/model/${encodeURIComponent(modelId)}/invoke`,
      {
        method: "POST",
        headers: headersFor(opts.attribution, "application/json"),
        body: JSON.stringify(payload),
        signal: controller.signal,
      },
      controller.signal,
      deadlineAt,
    );

    if (!resp.ok) await fail(resp);
    return await resp.json();
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", onAbort);
  }
}
